import { z } from 'zod';
import { phoneNumbersSchema } from './userProfileSchemas';
import {
  GENDER_FIELD_NAMES,
  GENDER_OPTION_VALUES,
  GENDER_OTHER_TEXT_MAX_LENGTH,
  GENDER_OTHER_VALUE,
  GENDER_MESSAGES,
} from '../constants/genderOptions';

export const loginRequestSchema = z.object({
  email: z.string({ required_error: 'Email is required' }).email('Enter a valid email address'),
  password: z.string({ required_error: 'Password is required' }).min(1, 'Password is required'),
});

const userSummarySchema = z.object({
  userId: z.number().int(),
  userName: z.string(),
  emailAddress: z.string().email(),
  roles: z.array(z.string()),
});

const loginDataSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string().optional(),
  expiresAt: z.string(),
  user: userSummarySchema,
});

export const loginResponseSchema = z.object({
  success: z.boolean(),
  data: loginDataSchema.nullable().optional(),
  message: z.string().nullable().optional(),
  errors: z.array(z.string()).nullable().optional(),
  metadata: z.unknown().optional(),
});

export const refreshResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string().optional(),
  expiresIn: z.number().int(),
  tokenType: z.string().optional(),
});

export const logoutResponseSchema = z.object({
  success: z.boolean().optional(),
  message: z.string().optional(),
});

export const createUserProfileRequestSchema = z.object({
  userName: z.string().min(3),
  firstName: z.string().min(1),
  middleName: z.string().nullable().optional(),
  lastName: z.string().min(1),
  suffix: z.string().nullable().optional(),
  gender: z.enum(GENDER_OPTION_VALUES),
  otherGender: z.string().trim().max(GENDER_OTHER_TEXT_MAX_LENGTH).nullable().optional(),
  dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date of birth must be YYYY-MM-DD'),
  emailAddress: z.string().email(),
  phone: z.string().min(10, 'Phone number must be at least 10 digits').nullable().optional(), // Legacy field - optional now (using phoneNumbers instead)
  phoneNumbers: phoneNumbersSchema.nullable().optional(), // New jsonb field - optional but should be provided when phone is not provided
  addressLine1: z.string().min(1),
  addressLine2: z.string().nullable().optional(),
  city: z.string().min(1),
  state: z.string().min(2).max(2),
  zip: z.string().min(5),
  password: z.string().min(8),
  role: z.string().min(1),
  npiNumber: z.string().nullable().optional(),
  createdBy: z.union([z.string(), z.number()]).nullable().optional(),
  entraObjectId: z.string().uuid().nullable().optional(),
  /** For clinic staff (Office Admin / Pre-Exam Preparer): validated provider's clinician_profile id. Backend creates clinic_staff_profile + clinic_staff_provider_map in same transaction. */
  clinicianProfileId: z.number().int().positive().nullable().optional(),
})
  .refine(
    (data) => {
      // Either phone (legacy) or phoneNumbers (new) must be provided
      return data.phone || (data.phoneNumbers && data.phoneNumbers.phones && data.phoneNumbers.phones.length > 0);
    },
    {
      message: 'Either phone or phoneNumbers must be provided',
      path: ['phoneNumbers'], // Show error on phoneNumbers field
    }
  )
  .refine(
    (data) => data.gender !== GENDER_OTHER_VALUE || Boolean(data.otherGender?.trim()),
    {
      message: GENDER_MESSAGES.SPECIFY_WHEN_OTHER_ZOD,
      path: [GENDER_FIELD_NAMES.OTHER_GENDER],
    }
  );

export const registrationResultSchema = z.object({
  success: z.boolean(),
  data: z
    .object({
      userId: z.number().int(),
      requiresVerification: z.boolean(),
      message: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  message: z.string().nullable().optional(),
  errors: z.array(z.string()).nullable().optional(),
});

export type LoginRequest = z.infer<typeof loginRequestSchema>;
export type LoginApiResponse = z.infer<typeof loginResponseSchema>;
export type LoginResponse = {
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
  user: {
    id: string;
    email: string;
    name?: string;
    roles?: string[];
  };
};
export type RefreshResponse = z.infer<typeof refreshResponseSchema>;
export type CreateUserProfileRequest = z.infer<typeof createUserProfileRequestSchema>;
export type RegistrationResult = z.infer<typeof registrationResultSchema>;
